// constant.js
const ON_CHANGE_DEBOUNCE_TIMER = 300;

const PUB_SUB_EVENTS = {
  cartUpdate: "cart-update",
  quantityUpdate: "quantity-update",
  variantChange: "variant-change",
  cartError: "cart-error",
  sectionRefreshed: "section-refreshed",
};

const SECTION_REFRESH_RESOURCE_TYPE = {
  product: "product",
};

// pubsub.js
let subscribers = {};

function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) {
    subscribers[eventName] = [];
  }

  subscribers[eventName] = [...subscribers[eventName], callback];

  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => {
      return cb !== callback;
    });
  };
}

function publish(eventName, data) {
  if (subscribers[eventName]) {
    subscribers[eventName].forEach((callback) => {
      callback(data);
    });
  }
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

// Match every .product-card-title within `group` to the height of the
// tallest title in that group, so the price row lines up at the same
// position on every card no matter how many lines an individual title
// wraps to. Scoped per-caller (e.g. one carousel instance) rather than
// applied document-wide, so it only affects the cards passed in.
function equalizeProductCardTitles(group) {
  const titles = group.querySelectorAll(".product-card-title");
  if (titles.length < 2) return;

  titles.forEach((title) => {
    title.style.minHeight = "";
  });

  const maxHeight = Math.max(
    ...Array.from(titles, (title) => title.offsetHeight),
  );

  titles.forEach((title) => {
    title.style.minHeight = `${maxHeight}px`;
  });
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    "ARROWUP",
    "ARROWDOWN",
    "ARROWLEFT",
    "ARROWRIGHT",
    "TAB",
    "ENTER",
    "SPACE",
    "ESCAPE",
    "HOME",
    "END",
    "PAGEUP",
    "PAGEDOWN",
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener("keydown", (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener("mousedown", (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    "focus",
    () => {
      if (currentFocusedElement)
        currentFocusedElement.classList.remove("focused");

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add("focused");
    },
    true,
  );
}

function getFocusableElements(container) {
  const currentFocusableElementsString =
    'a[href]:not([disabled]):not([tabindex^="-"]), button:not([disabled]):not([tabindex^="-"]), textarea:not([disabled]):not([tabindex^="-"]), input:not([disabled]):not([tabindex^="-"]), select:not([disabled]):not([tabindex^="-"])';

  return Array.from(
    container.querySelectorAll(currentFocusableElementsString),
  ).filter((ele) => ele.offsetWidth !== 0 || ele.offsetHeight !== 0);
}

const trapFocusHandlers = {};
function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  // console.log(elements);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === "INPUT" &&
    ["search", "text", "email", "url"].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
}

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + "pauseVideo" + '","args":""}',
      "*",
    );
  });
  document.querySelectorAll(".js-vimeo").forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  document.querySelectorAll("video").forEach((video) => video.pause());
  document.querySelectorAll("product-model").forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function scrolledCheck() {
  if (window.pageYOffset > 0) {
    document.body.classList.add("window-scrolled");
  } else {
    document.body.classList.remove("window-scrolled");
  }
}
scrolledCheck();
window.addEventListener("scroll", scrolledCheck);

function setShortViewportHeight() {
  const value = document.documentElement.clientHeight;
  const currentValue = getComputedStyle(
    document.documentElement,
  ).getPropertyValue("--100-svh");
  if (value != currentValue) {
    document.documentElement.style.setProperty("--100-svh", `${value}px`);
  }
}

function setDynamicViewportHeight() {
  const value = window.innerHeight;
  const currentValue = getComputedStyle(
    document.documentElement,
  ).getPropertyValue("--100-dvh");

  if (value != currentValue) {
    document.documentElement.style.setProperty("--100-dvh", `${value}px`);
  }
}

function setHeaderTopPosition() {
  const header = document.getElementById("header");
  if (!header) return;
  const value = header.getBoundingClientRect().top;
  const currentValue = getComputedStyle(
    document.documentElement,
  ).getPropertyValue("--header-top-position");

  if (value && value != currentValue) {
    document.documentElement.style.setProperty(
      "--header-top-position",
      `${value}px`,
    );
  }
}

function setHeaderBottomPosition() {
  const header = document.getElementById("header-wrapper");
  if (!header) return;
  const value = header.getBoundingClientRect().bottom;
  const currentValue = getComputedStyle(
    document.documentElement,
  ).getPropertyValue("--header-bottom-position");

  if (value && value != currentValue) {
    document.documentElement.style.setProperty(
      "--header-bottom-position",
      `${value}px`,
    );
  }
}

function setPromobarHeight() {
  const promobar = document.getElementById("promobar");
  if (!promobar) return;
  const value = promobar.getBoundingClientRect().height;
  const currentValue = getComputedStyle(
    document.documentElement,
  ).getPropertyValue("--promobar-height");

  if (value && value != currentValue) {
    document.documentElement.style.setProperty(
      "--promobar-height",
      `${value}px`,
    );
  }
}

function setHeaderHeight() {
  const header = document.getElementById("header");
  if (!header) return;
  const value = header.getBoundingClientRect().height;
  const currentValue = getComputedStyle(
    document.documentElement,
  ).getPropertyValue("--header-height");

  if (value && value != currentValue) {
    document.documentElement.style.setProperty("--header-height", `${value}px`);
  }
}

function setFooterHeight() {
  const footer = document.getElementById("footer");
  if (!footer) return;
  const value = footer.getBoundingClientRect().height;
  const currentValue = getComputedStyle(
    document.documentElement,
  ).getPropertyValue("--footer-height");

  if (value && value != currentValue) {
    document.documentElement.style.setProperty("--footer-height", `${value}px`);
  }
}

function setCustomPropertiesOnLoad() {
  setShortViewportHeight();
  setDynamicViewportHeight();

  setPromobarHeight();
  setHeaderHeight();
  setFooterHeight();

  setHeaderTopPosition();
  setHeaderBottomPosition();
}

function setCustomPropertiesOnScroll() {
  setPromobarHeight();
  setHeaderHeight();

  setHeaderTopPosition();
  setHeaderBottomPosition();
}

function setCustomPropertiesOnResize() {
  setShortViewportHeight();
  setDynamicViewportHeight();

  setPromobarHeight();
  setHeaderHeight();
  setFooterHeight();

  setHeaderTopPosition();
  setHeaderBottomPosition();
}

window.addEventListener("load", setCustomPropertiesOnLoad, false);

window.addEventListener(
  "scroll",
  debounce(setCustomPropertiesOnScroll, 300),
  false,
);

window.addEventListener(
  "resize",
  debounce(setCustomPropertiesOnResize, 300),
  false,
);

function checkFlexGap() {
  // create flex container with row-gap set
  var flex = document.createElement("div");
  flex.style.display = "flex";
  flex.style.flexDirection = "column";
  flex.style.rowGap = "1px";

  // create two, elements inside it
  flex.appendChild(document.createElement("div"));
  flex.appendChild(document.createElement("div"));

  // append to the DOM (needed to obtain scrollHeight)
  document.body.appendChild(flex);
  var isSupported = flex.scrollHeight === 1; // flex container should be 1px high from the row-gap
  flex.parentNode.removeChild(flex);

  return isSupported;
}
if (checkFlexGap()) {
  document.documentElement.classList.add("flexbox-gap");
} else {
  document.documentElement.classList.add("no-flexbox-gap");
}

document.querySelectorAll("details summary").forEach((summary) => {
  summary.setAttribute(
    "aria-expanded",
    summary.parentNode.hasAttribute("open"),
  );

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute(
      "aria-expanded",
      !event.currentTarget.closest("details").hasAttribute("open"),
    );
  });

  if (summary.closest("header-drawer")) return;
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

document.querySelectorAll(".accordion-group").forEach((item) => {
  item.addEventListener("click", function (event) {
    let currentAccordion = event.target.closest(".accordion-group-item");
    let currentSummary = event.target.closest(".accordion-group-item summary");
    let accordionGroup = event.currentTarget;

    if (!currentSummary) return;
    if (!accordionGroup.contains(currentSummary)) return;

    accordionGroup
      .querySelectorAll(".accordion-group-item")
      .forEach((accordion) => {
        if (accordion != currentAccordion) {
          accordion.removeAttribute("open");
        }
      });
  });
});

class HeaderMega extends HTMLElement {
  constructor() {
    super();

    this.dropdowns = this.querySelectorAll(".header-dropdown");
    this.dropdownToggles = this.querySelectorAll(".header-dropdown-toggle");
    this.dropdownMenus = this.querySelectorAll(".header-dropdown-menu");

    this.closeTimer = null;

    this.bindEvents();
  }

  bindEvents() {
    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onDropdownFocusOut.bind(this));

    this.dropdowns.forEach((item) => {
      item.addEventListener("mouseenter", this.onDropdownMouseEnter.bind(this));
      item.addEventListener("mouseleave", this.onDropdownMouseLeave.bind(this));
    });

    this.dropdownToggles.forEach((item) => {
      item.addEventListener("click", this.onDropdownToggleClick.bind(this));
    });
  }

  onDropdownToggleClick(event) {
    const isKeyboardClick = event.detail === 0;

    if (!isKeyboardClick) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    const dropdownToggle = event.currentTarget;
    const dropdown = dropdownToggle.parentNode;

    if (this.isDropdownOpen(dropdown)) {
      this.closeDropdown(dropdown);
      this.closeMega();
    } else {
      this.openDropdown(dropdown, true);
      this.openMega();
    }
  }

  onDropdownMouseEnter(event) {
    const dropdown = event.currentTarget;

    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    if (this.isDropdownOpen(dropdown)) return;

    if (this.hasDropdownOpen()) {
      this.closeAllDropdowns();
      this.openDropdown(dropdown, false);
    } else {
      this.openDropdown(dropdown, false);
      this.openMega();
    }
  }

  onDropdownMouseLeave(event) {
    const dropdown = event.currentTarget;

    this.closeTimer = setTimeout(() => {
      this.closeDropdown(dropdown);
      this.closeMega();
    }, 200);
  }

  onDropdownFocusOut(event) {
    if (!this.contains(event.relatedTarget)) {
      if (this.hasDropdownOpen()) {
        this.closeAllDropdowns();
        this.closeMega();
      }
    }
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = this.querySelector("details[open]");
    if (!openDetailsElement) return;

    this.closeDropdown(openDetailsElement);
    this.closeMega();

    // Focus back onto the toggle element after pressing Escape
    const toggle = openDetailsElement.querySelector(".header-dropdown-toggle");
    if (toggle) toggle.focus();
  }

  isDropdownOpen(detailsElement) {
    return (
      detailsElement.hasAttribute("open") &&
      detailsElement.classList.contains("open")
    );
  }

  hasDropdownOpen() {
    return this.classList.contains("dropdown-open");
  }

  openDropdown(detailsElement, shouldMoveFocus = false) {
    const summaryElement = detailsElement.querySelector("summary");

    this.closeAllDropdowns();

    detailsElement.setAttribute("open", "");
    detailsElement.classList.add("open");

    if (summaryElement) {
      summaryElement.setAttribute("aria-expanded", "true");

      if (shouldMoveFocus) {
        const firstLink = detailsElement.querySelector(
          "a, button, [tabindex='0']",
        );
        if (firstLink) {
          firstLink.focus();
        }
      }
    }

    this.classList.add("dropdown-open");
  }

  closeDropdown(detailsElement) {
    const summaryElement = detailsElement.querySelector("summary");

    detailsElement.removeAttribute("open");
    detailsElement.classList.remove("open");

    if (summaryElement) {
      summaryElement.setAttribute("aria-expanded", "false");
    }

    this.classList.remove("dropdown-open");
  }

  closeAllDropdowns() {
    this.dropdowns.forEach((item) => {
      if (this.isDropdownOpen(item)) {
        item.removeAttribute("open");
        item.classList.remove("open");
        const summary = item.querySelector("summary");
        if (summary) {
          summary.setAttribute("aria-expanded", "false");
          removeTrapFocus(summary);
        }
      }
    });
    this.classList.remove("dropdown-open");
  }

  openMega() {
    document.body.classList.add("header-mega-open");
  }

  closeMega() {
    document.body.classList.remove("header-mega-open");
  }
}

customElements.define("header-mega", HeaderMega);

class HeaderDrawer extends HTMLElement {
  constructor() {
    super();

    this.drawerContainer = this.querySelector(".header-drawer-container");
    this.drawerToggle = this.drawerContainer.querySelector(
      ".header-drawer-toggle",
    );
    // this.drawerCloseButton = this.drawerContainer.querySelector(
    //   ".header-drawer-close"
    // );
    this.drawer = this.drawerContainer.querySelector(".header-drawer");
    this.drawerLink = this.drawerContainer.querySelectorAll(
      ".header-drawer-menu-link",
    );

    this.subDrawerContainers = this.drawerContainer.querySelectorAll(
      ".header-child-drawer-container, .header-grand-drawer-container",
    );
    this.subDrawerToggles = this.drawerContainer.querySelectorAll(
      ".header-child-drawer-toggle, .header-grand-drawer-toggle",
    );
    this.subDrawers = this.drawerContainer.querySelectorAll(
      ".header-child-drawer, .header-grand-drawer",
    );
    this.subDrawerCloseButtons = this.drawerContainer.querySelectorAll(
      ".header-child-drawer-close, .header-grand-drawer-close",
    );

    this.bindEvents();
  }

  bindEvents() {
    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onDrawerFocusOut.bind(this));

    this.drawerToggle.addEventListener(
      "click",
      this.onDrawerToggleClick.bind(this),
    );
    // this.drawerCloseButton.addEventListener(
    //   "click",
    //   this.onDrawerCloseButtonClick.bind(this)
    // );

    this.subDrawerToggles.forEach((item) => {
      item.addEventListener("click", this.onSubDrawerToggleClick.bind(this));
    });

    this.subDrawerCloseButtons.forEach((item) => {
      item.addEventListener(
        "click",
        this.onSubDrawerCloseButtonClick.bind(this),
      );
    });

    // this.subDrawerContainers.forEach((item) => {
    //   item.addEventListener("focusout", this.onSubDrawerFocusOut.bind(this));
    // });
  }

  onDrawerToggleClick(event) {
    event.preventDefault();

    if (this.isDrawerOpen()) {
      this.closeDrawer();
    } else {
      this.openDrawer();
    }
  }

  onDrawerCloseButtonClick(event) {
    event.preventDefault();
    this.closeDrawer();
  }

  onSubDrawerToggleClick(event) {
    event.preventDefault();

    const subDrawerToggle = event.currentTarget;
    const subDrawerContainer = subDrawerToggle.parentNode;

    if (this.isSubDrawerOpen(subDrawerContainer)) {
      this.closeSubDrawer(subDrawerContainer);
    } else {
      this.openSubDrawer(subDrawerContainer);
    }
  }

  onSubDrawerCloseButtonClick(event) {
    event.preventDefault();

    const detailsElement = event.currentTarget.closest("details");
    this.closeSubDrawer(detailsElement);
  }

  onDrawerFocusOut(event) {
    const relatedTarget = event.relatedTarget;
    setTimeout(() => {
      if (this.isDrawerOpen() && !this.drawerContainer.contains(relatedTarget))
        this.closeDrawer();
    });
  }

  onSubDrawerFocusOut(event) {
    const subDrawerContainer = event.currentTarget;
    const relatedTarget = event.relatedTarget;
    setTimeout(() => {
      if (this.hasSubDrawerOpen() && relatedTarget === this.drawer)
        this.closeSubDrawer(subDrawerContainer);
    });
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    if (openDetailsElement === this.drawerContainer) {
      this.closeDrawer(this.drawerToggle);
    } else {
      this.closeSubDrawer(openDetailsElement);
    }
  }

  isDrawerOpen() {
    return (
      this.drawerContainer.hasAttribute("open") &&
      this.drawerContainer.classList.contains("open")
    );
  }

  isSubDrawerOpen(detailsElement) {
    return (
      detailsElement.hasAttribute("open") &&
      detailsElement.classList.contains("open")
    );
  }

  isSubDrawerClose(detailsElement) {
    return !detailsElement.hasAttribute("open");
  }

  hasSubDrawerOpen() {
    return this.drawerContainer.classList.contains("subdrawer-open");
  }

  openDrawer() {
    // open drawer
    setTimeout(() => {
      this.drawerContainer.setAttribute("open", "");
      this.drawerContainer.classList.add("open");
      this.drawerToggle.setAttribute("aria-expanded", true);
    });

    // trap focus
    trapFocus(this.drawerContainer, this.drawerToggle);

    // update status
    document.body.classList.add("header-drawer-open", "overflow-hidden");
  }

  openSubDrawer(detailsElement) {
    const summaryElement = detailsElement.querySelector("summary");

    // // close all sub drawers
    // this.closeAllSubDrawers();

    // open sub drawer
    setTimeout(() => {
      detailsElement.setAttribute("open", "");
      detailsElement.classList.add("open");
      summaryElement.setAttribute("aria-expanded", true);
    });

    // trap focus
    trapFocus(detailsElement, summaryElement);

    // update status
    detailsElement.closest("ul").parentNode.classList.add("subdrawer-open");
    // this.drawerContainer.classList.add("subdrawer-open");
  }

  closeDrawer(elementToFocus = false) {
    // remove trap focus
    removeTrapFocus(elementToFocus);

    // close drawer
    this.drawerContainer.classList.remove("open");
    this.drawerContainer.removeAttribute("open");
    this.drawerToggle.setAttribute("aria-expanded", false);

    // close all sub drawers
    this.closeAllSubDrawers();

    // update status
    document.body.classList.remove("header-drawer-open", "overflow-hidden");
  }

  closeSubDrawer(detailsElement) {
    const summaryElement = detailsElement.querySelector("summary");

    // remove trap focus
    removeTrapFocus(summaryElement);

    // close sub drawer
    detailsElement.classList.remove("open");
    detailsElement.removeAttribute("open");
    summaryElement.setAttribute("aria-expanded", false);

    // update status
    detailsElement.closest("ul").parentNode.classList.remove("subdrawer-open");
    // this.drawerContainer.classList.remove("subdrawer-open");
  }

  closeAllSubDrawers() {
    this.subDrawerContainers.forEach((item) => {
      if (this.isSubDrawerOpen(item)) {
        this.closeSubDrawer(item);
      }
    });
  }
}
customElements.define("header-drawer", HeaderDrawer);

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) =>
      summary.addEventListener("click", this.onSummaryClick.bind(this)),
    );
    this.querySelectorAll("[data-submenu-drawer-close]").forEach((button) =>
      button.addEventListener("click", this.onCloseButtonClick.bind(this)),
    );

    const menuDrawerClose = this.querySelector("[data-menu-drawer-close]");
    if (menuDrawerClose)
      menuDrawerClose.addEventListener(
        "click",
        this.closeMenuDrawer.bind(this),
      );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(
          event,
          this.mainDetailsToggle.querySelector("summary"),
        )
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(
        summaryElement.nextElementSibling,
        detailsElement.querySelector("button"),
      );
      summaryElement.nextElementSibling.removeEventListener(
        "transitionend",
        addTrapFocus,
      );
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen
        ? this.closeMenuDrawer(event, summaryElement)
        : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 990px)")) {
        document.documentElement.style.setProperty(
          "--viewport-height",
          `${window.innerHeight}px`,
        );
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("submenu-open");
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener(
              "transitionend",
              addTrapFocus,
            );
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add("overflow-hidden");
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle
      .querySelectorAll(".submenu-open")
      .forEach((submenu) => {
        submenu.classList.remove("submenu-open");
      });
    document.body.classList.remove("overflow-hidden");
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent)
      elementToFocus?.setAttribute("aria-expanded", false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute("open") &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(
            detailsElement.closest("details[open]"),
            detailsElement.querySelector("summary"),
          );
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}
customElements.define("menu-drawer", MenuDrawer);

class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector("details");
    this.summary = this.mainDetailsToggle.querySelector("summary");
    this.content = this.summary.nextElementSibling;

    this.mainDetailsToggle.addEventListener(
      "focusout",
      this.onFocusOut.bind(this),
    );
    this.mainDetailsToggle.addEventListener("toggle", this.onToggle.bind(this));
  }

  onFocusOut(event) {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      this.close();
    }
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute("open")) {
      this.animations.forEach((animation) => animation.play());
      trapFocus(this, this.summary);
      //document.body.classList.add("overflow-hidden");
    } else {
      this.animations.forEach((animation) => animation.cancel());
      removeTrapFocus(this.summary);
      //document.body.classList.remove("overflow-hidden");
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute("open");
    this.mainDetailsToggle
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
    //document.body.classList.remove("overflow-hidden");
  }
}
customElements.define("details-disclosure", DetailsDisclosure);

class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector("details");
    this.summaryToggle = this.querySelector("summary");

    this.detailsContainer.addEventListener(
      "keyup",
      (event) => event.code.toUpperCase() === "ESCAPE" && this.close(),
    );
    this.summaryToggle.addEventListener(
      "click",
      this.onSummaryClick.bind(this),
    );
    this.querySelector('button[type="button"]').addEventListener(
      "click",
      this.close.bind(this),
    );

    this.summaryToggle.setAttribute("role", "button");
  }

  isOpen() {
    return this.detailsContainer.hasAttribute("open");
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest("details").hasAttribute("open")
      ? this.close()
      : this.open(event);
  }

  onBodyClick(event) {
    if (
      !this.contains(event.target) ||
      event.target.classList.contains("modal-overlay")
    )
      this.close(false);
  }

  open(event) {
    this.onBodyClickEvent =
      this.onBodyClickEvent || this.onBodyClick.bind(this);
    event.target.closest("details").setAttribute("open", true);
    document.body.addEventListener("click", this.onBodyClickEvent);
    document.body.classList.add("overflow-hidden");

    trapFocus(
      this.detailsContainer.querySelector('[tabindex="-1"]'),
      this.detailsContainer.querySelector('input:not([type="hidden"])'),
    );
  }

  close(focusToggle = true) {
    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    this.detailsContainer.removeAttribute("open");
    document.body.removeEventListener("click", this.onBodyClickEvent);
    document.body.classList.remove("overflow-hidden");
  }
}
customElements.define("details-modal", DetailsModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", () => {
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });
  }
}
customElements.define("modal-opener", ModalOpener);

class DialogModal extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      "click",
      this.hide.bind(this, false),
    );
    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (
          event.pointerType === "mouse" &&
          !event.target.closest("deferred-media, product-model")
        )
          this.hide();
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector(".template-popup");
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    this.querySelector('[role="dialog"]').classList.add("active");
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    if (
      this.querySelector('[role="dialog"]').classList.contains(
        "dialog-modal-dialog--animation",
      )
    ) {
      this.querySelector('[role="dialog"]').addEventListener(
        "transitionend",
        () => {
          document.body.classList.remove("overflow-hidden");
          document.body.dispatchEvent(new CustomEvent("modalClosed"));
          this.removeAttribute("open");
          removeTrapFocus(this.openedBy);
          window.pauseAllMedia();
        },
        { once: true },
      );

      this.querySelector('[role="dialog"]').classList.remove("active");
    } else {
      document.body.classList.remove("overflow-hidden");
      document.body.dispatchEvent(new CustomEvent("modalClosed"));
      this.querySelector('[role="dialog"]').classList.remove("active");
      this.removeAttribute("open");
      removeTrapFocus(this.openedBy);
      window.pauseAllMedia();
    }
  }
}
customElements.define("dialog-modal", DialogModal);

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector("form");
    facetForm.addEventListener("input", this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector("#FacetsWrapperDesktop");
    if (facetWrapper) facetWrapper.addEventListener("keyup", onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById("ProductCount");
    const countContainerDesktop = document.getElementById(
      "ProductCountDesktop",
    );
    const loadingSpinners = document.querySelectorAll(
      ".facets-container .loading__spinner, facet-filters-form .loading__spinner",
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove("hidden"));
    document
      .getElementById("ProductGridContainer")
      .querySelector(".collection")
      .classList.add("loading");
    if (countContainer) {
      countContainer.classList.add("loading");
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add("loading");
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url },
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === "function")
          initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === "function")
      initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById("ProductGridContainer").innerHTML = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductGridContainer").innerHTML;

    document
      .getElementById("ProductGridContainer")
      .querySelectorAll(".scroll-trigger")
      .forEach((element) => {
        element.classList.add("scroll-trigger--cancel");
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductCount").innerHTML;
    const container = document.getElementById("ProductCount");
    const containerDesktop = document.getElementById("ProductCountDesktop");
    container.innerHTML = count;
    container.classList.remove("loading");
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove("loading");
    }
    const loadingSpinners = document.querySelectorAll(
      ".facets-container .loading__spinner, facet-filters-form .loading__spinner",
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add("hidden"));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter",
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter",
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (
        !Array.from(facetDetailsElementsFromFetch).some(
          ({ id }) => currentElement.id === id,
        )
      ) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter(
      (element) => !matchesId(element),
    );
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(
      matchesId,
    );

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML =
          elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } =
            facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document
            .querySelector(`#${elementToRender.parentElement.id} .js-filter`)
            .before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest(".js-filter").id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(
          countsToRender,
          event.target.closest(".js-filter"),
        );
        FacetFiltersForm.renderMobileCounts(
          countsToRender,
          document.getElementById(closestJSFilterID),
        );

        const newFacetDetailsElement =
          document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains(
          "mobile-facets__details",
        )
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate =
          newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute("type") === "text";

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [
      ".active-facets-mobile",
      ".active-facets-desktop",
    ];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML =
        activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [
      ".mobile-facets__open",
      ".mobile-facets__count",
      ".sorting",
    ];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML =
        html.querySelector(selector).innerHTML;
    });

    document
      .getElementById("FacetFiltersFormMobile")
      .closest("menu-drawer")
      .bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector(".facets__summary");
    const sourceSummary = source.querySelector(".facets__summary");

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector(".facets__header");
    const sourceHeaderElement = source.querySelector(".facets__header");

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector(".facets-wrap");
    const sourceWrapElement = source.querySelector(".facets-wrap");

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(
        target.querySelector("show-more-button .label-show-more.hidden"),
      );
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll(".facets__item.hidden")
          .forEach((hiddenItem) =>
            hiddenItem.classList.replace("hidden", "show-more-item"),
          );
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector(".mobile-facets__list");
    const sourceFacetsList = source.querySelector(".mobile-facets__list");

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`,
    );
  }

  static getSections() {
    return [
      {
        section: document.getElementById("product-grid").dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll(
      "facet-filters-form form",
    );
    if (event.srcElement.className == "mobile-facets__checkbox") {
      const searchParams = this.createSearchParams(
        event.target.closest("form"),
      );
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile =
        event.target.closest("form").id === "FacetFiltersFormMobile";

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (
            form.id === "FacetSortForm" ||
            form.id === "FacetFiltersForm" ||
            form.id === "FacetSortDrawerForm"
          ) {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === "FacetFiltersFormMobile") {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join("&"), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf("?") == -1
        ? ""
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf("?") + 1,
          );
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll("input").forEach((element) => {
      element.addEventListener("change", this.onRangeChange.bind(this));
      element.addEventListener("keydown", this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern =
      /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll("input");
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute("data-max", maxInput.value);
    if (minInput.value) maxInput.setAttribute("data-min", minInput.value);
    if (minInput.value === "") maxInput.setAttribute("data-min", 0);
    if (maxInput.value === "")
      minInput.setAttribute("data-max", maxInput.getAttribute("data-max"));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute("data-min"));
    const max = Number(input.getAttribute("data-max"));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}
customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector("a");
    facetLink.setAttribute("role", "button");
    facetLink.addEventListener("click", this.closeFilter.bind(this));
    facetLink.addEventListener("keyup", (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form =
      this.closest("facet-filters-form") ||
      document.querySelector("facet-filters-form");
    form.onActiveFilterClick(event);
  }
}
customElements.define("facet-remove", FacetRemove);

class ProductGallery extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector(".product-gallery-slider");
    this.thumbSlider = this.querySelector(".product-gallery-thumbSlider");
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.initThumbSlider();
    this.initSlider();
  }

  initThumbSlider() {
    this.thumbSwiper = new Swiper(this.thumbSlider.querySelector(".swiper"), {
      direction: "horizontal",
      speed: 400,
      grabCursor: true,
      slidesPerView: 5,
      spaceBetween: 24,
      centerInsufficientSlides: true,
      watchSlidesProgress: true,
      mousewheel: {
        forceToAxis: true,
      },
      navigation: {
        prevEl: this.thumbSlider.querySelector(".swiper-nav-button--prev"),
        nextEl: this.thumbSlider.querySelector(".swiper-nav-button--next"),
        disabledClass: "hidden",
      },
    });
  }

  initSlider() {
    this.sliderSwiper = new Swiper(this.slider, {
      loop: true,
      speed: 400,
      slidesPerView: 1,
      spaceBetween: 0,
      grabCursor: true,
      mousewheel: {
        forceToAxis: true,
      },
      navigation: {
        prevEl: this.slider.querySelector(".swiper-nav-button--prev"),
        nextEl: this.slider.querySelector(".swiper-nav-button--next"),
        disabledClass: "hidden",
      },
      keyboard: {
        enabled: true,
        onlyInViewport: true,
        pageUpDown: true,
      },
      thumbs: {
        swiper: this.thumbSwiper,
      },
      on: {
        activeIndexChange: function (swiper) {
          const currentSlideVideo =
            swiper.slides[swiper.activeIndex].querySelector("video");
          const previousSlideVideo =
            swiper.slides[swiper.previousIndex].querySelector("video");
          if (previousSlideVideo) {
            previousSlideVideo.pause();
          }
          if (currentSlideVideo) {
            currentSlideVideo.play();
          }
        },
      },
    });
  }

  destroySlider(swiper) {
    swiper.destroy(true, true);
  }
}
customElements.define("product-gallery", ProductGallery);

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("product-recommendations");

        if (recommendations && recommendations.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        this.carousel = this.querySelector(".swiper");

        this.swiper = new Swiper(this.carousel, {
          loop: false,
          grabCursor: true,
          speed: 400,
          threshold: 6,
          slidesPerView: 1.5,
          spaceBetween: 20,
          slidesOffsetBefore: 24,
          slidesOffsetAfter: 24,
          mousewheel: {
            forceToAxis: true,
          },
          navigation: {
            prevEl: this.carousel.querySelector(".swiper-nav-button--prev"),
            nextEl: this.carousel.querySelector(".swiper-nav-button--next"),
            disabledClass: "hidden",
          },
          breakpoints: {
            768: {
              slidesPerView: 2.4,
              slidesOffsetBefore: 30,
              slidesOffsetAfter: 30,
            },
            992: {
              slidesPerView: 3.4,
              slidesOffsetBefore: 60,
              slidesOffsetAfter: 60,
            },
            1200: {
              slidesPerView: 3.4,
              slidesOffsetBefore: 60,
              slidesOffsetAfter: 60,
            },
          },
        });
      })
      .catch((e) => {
        console.error(e);
      });
  }
}
customElements.define("product-recommendations", ProductRecommendations);

class PromoBar extends HTMLElement {
  constructor() {
    super();

    this.slider = this.querySelector(".swiper");
    this.sliderAutoplayInterval = this.slider.dataset.sliderAutoplayInterval;
  }
  connectedCallback() {
    this.swiper = new Swiper(this.slider, {
      loop: true,
      speed: 400,
      allowTouchMove: false,
      followFinger: false,
      simulateTouch: false,
      effect: "fade",
      navigation: {
        prevEl: this.slider.querySelector(".swiper-nav-button--prev"),
        nextEl: this.slider.querySelector(".swiper-nav-button--next"),
        disabledClass: "hidden",
      },
      fadeEffect: {
        crossFade: true,
      },
      autoplay: {
        delay: this.sliderAutoplayInterval,
        pauseOnMouseEnter: false,
        disableOnInteraction: false,
      },
    });
  }
}
customElements.define("promo-bar", PromoBar);

class CollectionNavCarousel extends HTMLElement {
  constructor() {
    super();

    this.slider = this.querySelector(".swiper");
  }
  connectedCallback() {
    this.swiper = new Swiper(this.slider, {
      speed: 400,
      grabCursor: true,
      slidesPerView: "auto",
      spaceBetween: 12,
      mousewheel: {
        forceToAxis: true,
      },
      navigation: {
        prevEl: this.slider.querySelector(".swiper-nav-button--prev"),
        nextEl: this.slider.querySelector(".swiper-nav-button--next"),
        disabledClass: "hidden",
      },
    });
  }
}
customElements.define("collection-nav-carousel", CollectionNavCarousel);

class HomePageSlider extends HTMLElement {
  constructor() {
    super();

    this.slider = this.querySelector(".swiper");

    this.sliderAutoplayEnable =
      this.slider.dataset.sliderAutoplayEnable === "true";
    this.sliderAutoplayInterval = this.slider.dataset.sliderAutoplayInterval;
  }
  connectedCallback() {
    this.swiper = new Swiper(this.slider, {
      loop: true,
      speed: 400,
      slidesPerView: 1,
      spaceBetween: 0,
      grabCursor: true,
      autoplay: this.sliderAutoplayEnable
        ? {
            delay: this.sliderAutoplayInterval,
            pauseOnMouseEnter: false,
            disableOnInteraction: true,
          }
        : false,
      mousewheel: {
        forceToAxis: true,
      },
      navigation: {
        prevEl: this.slider.querySelector(".swiper-nav-button--prev"),
        nextEl: this.slider.querySelector(".swiper-nav-button--next"),
        disabledClass: "hidden",
      },
      pagination: {
        el: this.slider.querySelector(".swiper-pagination"),
        type: "custom",
        renderCustom: function (swiper, current, total) {
          return current + " | " + total;
        },
      },
      keyboard: {
        enabled: true,
        onlyInViewport: true,
        pageUpDown: true,
      },
      on: {
        slideChangeTransitionStart: function (swiper, speed, internal) {
          // get the control color of the next slide
          const activeSlide = swiper.slides[swiper.activeIndex];
          const activeSlideControlColor =
            activeSlide.dataset.sliderControlColor;
          // spread the control color to slider
          swiper.el.style.setProperty(
            "--swiper-theme-color",
            activeSlideControlColor,
          );
          swiper.el.style.setProperty(
            "--swiper-navigation-color",
            activeSlideControlColor,
          );
        },
      },
    });
  }
}
customElements.define("homepage-slider", HomePageSlider);

class HomePageProductCarousel extends HTMLElement {
  constructor() {
    super();

    this.carousel = this.querySelector(".swiper");
  }
  connectedCallback() {
    const slidesMobile = parseFloat(this.dataset.slidesMobile) || 1.5;
    const slidesTablet = parseFloat(this.dataset.slidesTablet) || 2.3;
    const slidesDesktop = parseFloat(this.dataset.slidesDesktop) || 3.3;
    const slidesLarge = parseFloat(this.dataset.slidesLarge) || 4.2;
    const slidesXlarge = parseFloat(this.dataset.slidesXlarge) || 5.5;
    const mobileFullyVisible = slidesMobile >= 2;
    const outer = this.querySelector(".hp-product-carousel-swiper-outer");
    if (outer) {
      outer.classList.toggle(
        "hp-product-carousel-swiper-outer--full-mobile",
        mobileFullyVisible,
      );
    }

    this.swiper = new Swiper(this.carousel, {
      loop: false,
      grabCursor: true,
      speed: 400,
      threshold: 6,
      slidesPerView: slidesMobile,
      spaceBetween: 20,
      ...(mobileFullyVisible
        ? {}
        : { slidesOffsetBefore: 24, slidesOffsetAfter: 24 }),
      mousewheel: {
        forceToAxis: true,
      },
      navigation: {
        prevEl: this.querySelector(".swiper-nav-button--prev"),
        nextEl: this.querySelector(".swiper-nav-button--next"),
        disabledClass: "disabled",
      },
      pagination: {
        el: this.querySelector(".hp-product-carousel-pagination"),
        clickable: true,
      },
      breakpoints: {
        768: {
          slidesPerView: slidesTablet,
          slidesOffsetBefore: 30,
          slidesOffsetAfter: 30,
        },
        992: {
          slidesPerView: slidesDesktop,
          slidesOffsetBefore: 60,
          slidesOffsetAfter: 60,
        },
        1200: {
          slidesPerView: slidesLarge,
          slidesOffsetBefore: 20,
          slidesOffsetAfter: 20,
        },
        1792: {
          slidesPerView: slidesXlarge,
          slidesOffsetBefore: 20,
          slidesOffsetAfter: 20,
        },
      },
      on: {
        resize: () => equalizeProductCardTitles(this.carousel),
      },
    });

    equalizeProductCardTitles(this.carousel);
    window.addEventListener("load", () =>
      equalizeProductCardTitles(this.carousel),
    );
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() =>
        equalizeProductCardTitles(this.carousel),
      );
    }
  }
}
customElements.define("homepage-product-carousel", HomePageProductCarousel);

class ArticleRelatedArticles extends HTMLElement {
  constructor() {
    super();

    this.carousel = this.querySelector(".swiper");
  }
  connectedCallback() {
    this.swiper = new Swiper(this.carousel, {
      loop: false,
      grabCursor: true,
      speed: 400,
      threshold: 6,
      slidesPerView: 1.5,
      spaceBetween: 20,
      slidesOffsetBefore: 24,
      slidesOffsetAfter: 24,
      mousewheel: {
        forceToAxis: true,
      },
      navigation: {
        prevEl: this.carousel.querySelector(".swiper-nav-button--prev"),
        nextEl: this.carousel.querySelector(".swiper-nav-button--next"),
        disabledClass: "hidden",
      },
      breakpoints: {
        768: {
          slidesPerView: 2.3,
          slidesOffsetBefore: 30,
          slidesOffsetAfter: 30,
        },
        992: {
          slidesPerView: 3.3,
          slidesOffsetBefore: 60,
          slidesOffsetAfter: 60,
        },
        1200: {
          slidesPerView: 3.3,
          slidesOffsetBefore: 60,
          slidesOffsetAfter: 60,
        },
      },
    });
  }
}
customElements.define("article-related-articles", ArticleRelatedArticles);
class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener("click", this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute("loaded")) {
      const content = document.createElement("div");
      content.appendChild(
        this.querySelector("template").content.firstElementChild.cloneNode(
          true,
        ),
      );

      this.setAttribute("loaded", true);
      const deferredElement = this.appendChild(
        content.querySelector("video, model-viewer, iframe"),
      );
      if (focus) deferredElement.focus();
      if (
        deferredElement.nodeName == "VIDEO" &&
        deferredElement.getAttribute("autoplay")
      ) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}
customElements.define("deferred-media", DeferredMedia);

class VideojsSimpleControl extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.video = this.querySelector("video-js");
    this.aspectRatio = this.dataset.aspectRatio;

    this.videojsPlayer = videojs(this.video, {
      aspectRatio: this.aspectRatio,
      fluid: true,
      responsive: true,
      inactivityTimeout: 0,
      bigPlayButton: false,
      controlBar: {
        volumePanel: false,
        muteToggle: true,
        subsCapsButton: false,
        progressControl: false,
        remainingTimeDisplay: false,
        pictureInPictureToggle: false,
        fullscreenToggle: false,
      },
    }).player();

    this.observer = new IntersectionObserver(
      function (entries) {
        if (entries[0]["isIntersecting"] === true) {
        } else {
          this.videojsPlayer.muted(true);
        }
      }.bind(this),
      { root: null, rootMargin: "0px", threshold: 0 },
    );

    this.observer.observe(this);
  }
}
customElements.define("video-js-simple-control", VideojsSimpleControl);

class VideojsFullControl extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.video = this.querySelector("video-js");
    this.aspectRatio = this.dataset.aspectRatio;

    this.videojsPlayer = videojs(this.video, {
      aspectRatio: this.aspectRatio,
      fluid: true,
      responsive: true,
      inactivityTimeout: 0,
      bigPlayButton: true,
      controlBar: {
        subsCapsButton: false,
        pictureInPictureToggle: false,
      },
    }).player();

    this.observer = new IntersectionObserver(
      function (entries) {
        if (entries[0]["isIntersecting"] === true) {
        } else {
          this.videojsPlayer.muted(true);
        }
      }.bind(this),
      { root: null, rootMargin: "0px", threshold: 0 },
    );

    this.observer.observe(this);
  }
}
customElements.define("video-js-full-control", VideojsFullControl);

class VideojsNoControl extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.video = this.querySelector("video-js");
    this.videojsPlayer = videojs(this.video, {
      aspectRatio: "1:1",
      fluid: true,
      responsive: true,
      inactivityTimeout: 0,
      bigPlayButton: false,
      controls: false,
    }).player();
  }
}
customElements.define("video-js-no-control", VideojsNoControl);

class BisForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector("form");
    this.variantIdInput = this.form.querySelector(
      'input[name="bis_variant_id"]',
    );
    this.productIdInput = this.form.querySelector(
      'input[name="bis_product_id"]',
    );
    this.emailInput = this.form.querySelector('input[name="bis_email"]');
    ((this.success_msg = this.form.querySelector(".form-message--success")),
      (this.error_msg = this.form.querySelector(".form-message--error")));
  }
  connectedCallback() {
    this.form.addEventListener(
      "submit",
      function (event) {
        event.preventDefault();
        const email = this.emailInput.value,
          productId = this.productIdInput.value,
          variantId = this.variantIdInput.value;
        BIS.create(email, variantId, productId).then((data) => {
          if (data.status == "OK") {
            this.success_msg.classList.add("active");
          } else {
            this.error_msg.classList.remove("active");
          }
          setTimeout(() => {
            this.success_msg.classList.remove("active");
            this.error_msg.classList.remove("active");
          }, 5000);
        });
      }.bind(this),
    );
  }
}
customElements.define("bis-form", BisForm);

class SelectRadio extends HTMLElement {
  constructor() {
    super();

    this.button = this.querySelector("button");
    this.list = this.button.nextElementSibling;
    this.radios = this.list.querySelectorAll('input[type="radio"]');

    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onContainerKeyUp.bind(this));
    this.button.addEventListener("click", this.onToggle.bind(this));

    this.radios.forEach((radio) => {
      radio.addEventListener("click", this.onRadioClick.bind(this));
      radio.addEventListener("keydown", this.onRadioKeyDown.bind(this));
    });
  }

  onFocusOut(event) {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onContainerKeyUp(event) {
    if (event.code.toUpperCase() == "ESCAPE") {
      this.close();
      this.button.focus();
    }
  }

  onToggle(event) {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  onRadioClick(event) {
    const radio = event.currentTarget;
    const label = radio.nextElementSibling;
    const labelContent = label.innerHTML;
    this.button.querySelector("span").innerHTML = labelContent;

    if (event.clientX === 0 && event.clientY === 0) {
    } else {
      this.close();
    }
  }

  onRadioKeyDown(event) {
    if (
      event.code.toUpperCase() === "ENTER" ||
      event.code.toUpperCase() === "SPACE"
    ) {
      event.preventDefault();
      this.close();
      this.button.focus();
    }
  }

  isOpen() {
    return this.hasAttribute("open");
  }

  open(event) {
    const checkedRadio = Array.from(this.radios).find((radio) => radio.checked);
    this.setAttribute("open", true);
    this.button.setAttribute("aria-expanded", true);
    checkedRadio.focus();
  }

  close(event) {
    this.removeAttribute("open");
    this.button.setAttribute("aria-expanded", false);
  }
}
customElements.define("select-radio", SelectRadio);

class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      this.input.form.addEventListener("reset", this.onFormReset.bind(this));
      this.input.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this),
      );
    }
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains("hidden");
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove("hidden");
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add("hidden");
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = "";
      this.input.focus();
      this.toggleResetButton();
    }
  }
}
customElements.define("search-form", SearchForm);

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]",
    );
    this.allPredictiveSearchInstances =
      document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener("focus", this.onFocus.bind(this));
    if (window.matchMedia("(min-width: 768px)").matches) {
      this.addEventListener("focusout", this.onFocusOut.bind(this));
    }
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onCloseButtonClick(event) {
    this.close(true);
    this.toggleResetButton();
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close(true);
        this.toggleResetButton();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]",
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item"),
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]',
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm,
      )}&section_id=predictive-search`,
      {
        signal: this.abortController.signal,
      },
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close(true);
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          },
        );
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close(true);
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        .textContent,
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document.querySelector(".section--header").getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");
  }
}
customElements.define("predictive-search", PredictiveSearch);

class MainSearch extends SearchForm {
  constructor() {
    super();
    this.allSearchInputs = document.querySelectorAll('input[type="search"]');
    this.setupEventListeners();
  }

  setupEventListeners() {
    let allSearchForms = [];
    this.allSearchInputs.forEach((input) => allSearchForms.push(input.form));
    this.input.addEventListener("focus", this.onInputFocus.bind(this));
    if (allSearchForms.length < 2) return;
    allSearchForms.forEach((form) =>
      form.addEventListener("reset", this.onFormReset.bind(this)),
    );
    this.allSearchInputs.forEach((input) =>
      input.addEventListener("input", this.onInput.bind(this)),
    );
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.keepInSync("", this.input);
    }
  }

  onInput(event) {
    const target = event.target;
    this.keepInSync(target.value, target);
  }

  onInputFocus() {
    const isSmallScreen = window.innerWidth < 750;
    if (isSmallScreen) {
      this.scrollIntoView({ behavior: "smooth" });
    }
  }

  keepInSync(value, target) {
    this.allSearchInputs.forEach((input) => {
      if (input !== target) {
        input.value = value;
      }
    });
  }
}

customElements.define("main-search", MainSearch);

class ShowMoreButton extends HTMLElement {
  constructor() {
    super();
    const button = this.querySelector("button");
    button.addEventListener("click", (event) => {
      this.expandShowMore(event);
      const nextElementToFocus = event.target
        .closest(".parent-display")
        .querySelector(".show-more-item");
      if (
        nextElementToFocus &&
        !nextElementToFocus.classList.contains("hidden") &&
        nextElementToFocus.querySelector("input")
      ) {
        nextElementToFocus.querySelector("input").focus();
      }
    });
  }
  expandShowMore(event) {
    const parentDisplay = event.target
      .closest('[id^="Show-More-"]')
      .closest(".parent-display");
    const parentWrap = parentDisplay.querySelector(".parent-wrap");
    this.querySelectorAll(".label-text").forEach((element) =>
      element.classList.toggle("hidden"),
    );
    parentDisplay
      .querySelectorAll(".show-more-item")
      .forEach((item) => item.classList.toggle("hidden"));
    if (!this.querySelector(".label-show-less")) {
      this.classList.add("hidden");
    }
  }
}
customElements.define("show-more-button", ShowMoreButton);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector(".icon");
  }

  connectedCallback() {
    document.addEventListener(
      "storefront:signincompleted",
      this.handleStorefrontSignInCompleted.bind(this),
    );
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}
customElements.define("account-icon", AccountIcon);

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == "undefined") {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent("on" + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options["method"] || "post";
  var params = options["parameters"] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (
  country_domid,
  province_domid,
  options,
) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(
    options["hideElement"] || province_domid,
  );

  Shopify.addListener(
    this.countryEl,
    "change",
    Shopify.bind(this.countryHandler, this),
  );

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute("data-default");
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute("data-provinces");
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = "none";
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement("option");
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement("option");
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};



    class CategoryCarousel extends HTMLElement {
      constructor() {
        super();

        this.swiper = null;
        this.initialized = false;
        this.initializationAttempts = 0;
        this.maximumInitializationAttempts = 50;

        this.handleSectionLoad = this.handleSectionLoad.bind(this);
        this.handleSectionUnload = this.handleSectionUnload.bind(this);
        this.handleBlockSelect = this.handleBlockSelect.bind(this);
      }

      connectedCallback() {
        if (this.initialized) return;

        this.cacheElements();
        this.bindThemeEditorEvents();
        this.waitForSwiper();
      }

      disconnectedCallback() {
        this.destroy();
        this.unbindThemeEditorEvents();
      }

      cacheElements() {
        this.sliderElement = this.querySelector('.category-carousel__slider');

        this.slides = this.querySelectorAll('[data-category-slide]');
      }

      waitForSwiper() {
        if (typeof window.Swiper === 'function') {
          this.initialize();
          return;
        }

        this.initializationAttempts += 1;

        if (this.initializationAttempts >= this.maximumInitializationAttempts) {
          console.warn(`Swiper could not be initialized for category carousel ${this.dataset.sectionId}.`);

          return;
        }

        window.setTimeout(() => {
          this.waitForSwiper();
        }, 100);
      }

      initialize() {
        if (this.swiper || !this.sliderElement || !this.slides.length) {
          return;
        }

        this.swiper = new window.Swiper(this.sliderElement, this.getConfiguration());

        this.classList.add('is-initialized');
        this.initialized = true;
      }

      getConfiguration() {
        const autoplayEnabled = this.toBoolean(this.dataset.autoplay);

        const configuration = {
          slidesPerView: this.toNumber(this.dataset.slidesMobile, 4.5),
          spaceBetween: 12,
          speed: 500,
          centerInsufficientSlides: true,
          watchOverflow: true,
          observer: true,
          observeParents: true,
          grabCursor: true,
          freeMode: {
            enabled: false,
            momentum: true,
          },
          mousewheel: {
            forceToAxis: true,
            releaseOnEdges: true,
          },
          keyboard: {
            enabled: true,
            onlyInViewport: true,
          },
          breakpoints: {
            750: {
              slidesPerView: this.toNumber(this.dataset.slidesTablet, 5),
              spaceBetween: 20,
            },
            990: {
              slidesPerView: this.toNumber(this.dataset.slidesDesktop, 5),
              spaceBetween: 32,
            },
          },
        };

        if (autoplayEnabled) {
          configuration.autoplay = {
            delay: this.toNumber(this.dataset.autoplayDelay, 4000),
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          };
        }

        return configuration;
      }

      handleBlockSelect(event) {
        if (!this.swiper) return;

        const selectedSlide = event.target.closest('[data-category-slide]');

        if (!selectedSlide) return;

        const slideIndex = Array.from(this.slides).indexOf(selectedSlide);

        if (slideIndex < 0) return;

        this.swiper.slideTo(slideIndex);

        if (this.swiper.autoplay) {
          this.swiper.autoplay.stop();
        }
      }

      handleSectionLoad(event) {
        if (event.detail.sectionId !== this.dataset.sectionId) {
          return;
        }

        this.destroy();
        this.cacheElements();
        this.waitForSwiper();
      }

      handleSectionUnload(event) {
        if (event.detail.sectionId !== this.dataset.sectionId) {
          return;
        }

        this.destroy();
      }

      bindThemeEditorEvents() {
        if (!window.Shopify || !Shopify.designMode) return;

        document.addEventListener('shopify:section:load', this.handleSectionLoad);

        document.addEventListener('shopify:section:unload', this.handleSectionUnload);

        this.addEventListener('shopify:block:select', this.handleBlockSelect);
      }

      unbindThemeEditorEvents() {
        document.removeEventListener('shopify:section:load', this.handleSectionLoad);

        document.removeEventListener('shopify:section:unload', this.handleSectionUnload);

        this.removeEventListener('shopify:block:select', this.handleBlockSelect);
      }

      destroy() {
        if (this.swiper) {
          this.swiper.destroy(true, true);
          this.swiper = null;
        }

        this.classList.remove('is-initialized');

        this.initialized = false;
        this.initializationAttempts = 0;
      }

      toBoolean(value) {
        return value === true || value === 'true';
      }

      toNumber(value, fallback) {
        const number = Number.parseFloat(value);

        return Number.isNaN(number) ? fallback : number;
      }
    }

    if (!customElements.get('category-carousel')) {
      customElements.define('category-carousel', CategoryCarousel);
    }